import { FormGroup, makeStyles, Typography } from "@material-ui/core";
import { isAfter } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { ROWS_PER_PAGE } from "../App";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { AppDialog } from "../components/AppDialog";
import { AppToolbar } from "../components/AppToolbar";
import AssignationTransferDialog from "../components/AssignationTransferDialog";
import { DateFieldComponent } from "../components/DatePicker";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import { SelectComponent } from "../components/SelectComponent";
import { SimpleAutoComplete } from "../components/SimpleAutoComplete";
import { SpinnerWrapper } from "../components/SpinnerWrapper";
import { AutoCompleteComponent } from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import useAssignationList from "../hooks/useAssignationList";
import useWindowSize from "../hooks/useWindowSize";
import Assignation from "../models/Assignation";
import { Routes } from "../types/Routes";
import { ColorUtils } from "../utils/ColorUtils";
import { AssignationStatusFilters } from "../utils/enums/filters/AssignationStatusFilters";
import OpsDateUtils from "../utils/OpsDateUtils";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import RoleUtils from "../utils/RoleUtils";
import { SwitchCustom } from "../utils/SwitchCustom";
import useTableHeight from "../hooks/useTableHeight";
import useUpdateUrl from "../hooks/useUpdateUrl";
import AssignationEditionFields from "../components/EditionDialogs/Fields/AssignationEditionFields";
import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";

import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoveUpIcon from '@mui/icons-material/MoveUp';
import { EditDialog } from "../external/ensolvers-core-frontend-mui/components/EditDialog";
import { DialogPosition } from "../external/ensolvers-core-frontend-mui/components/Common/DialogPosition";

import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { useStyles as formStyles } from "../components/Main/formStyles";
import { Box } from "@mui/material";

export default function AssignationList() {
  const { t } = useTranslation();
  const {
    projectsNames,
    positionNames,
    userNames,
    canEdit,
    update,
    setUpdate,
    selectedAssignation,
    transferAssignation,
    assigned,
    handleChangeAssignedProjectsByPosittion,
    handleOnDeleteAssignation,
    handleOnCreate,
    handleOnUpdate,
    setSelectedAssignation,
    setTransferAssignation,
    getAssignations,
    statusFilter,
    setStatusFilter,
    isLoading,
    handleTransferAssignation,
    handleTransferProjectChange,
    transferPositionNames,
    transferProject,
    managerProjects,
    disableSaveButton,
    setPositionExternalId,
    setAssignationEndDate,
    setAssignationStartDate,
    setDisableSaveButton,
    selectedProject,
    setSelectedProject,
    setStoredProject,
  } = useAssignationList();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();
  const searchParams = new URLSearchParams(useLocation().search);
  const { updateUrl } = useUpdateUrl(Routes.ASSIGNATIONS, searchParams);
  const [selectedEditElement, setSelectedEditElement] = useState<Assignation | undefined>(undefined);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const classes = useStyles();
  const customStyles = formStyles();

  const renderProjectNameAutoComplete = (
    <SimpleAutoComplete
      label="Project Name"
      items={projectsNames}
      value={selectedProject ? selectedProject : EMPTY_AUTOCOMPLETE}
      onChange={(newValue) => {
        setSelectedProject(newValue);
        searchParams.set("project", newValue.externalId);
        setStoredProject(newValue);
        updateUrl();
      }}
    />
  );

  const renderFilters = () => {
    const mobileFilters = (
      <div className={classes.mobileFilters}>
        {renderProjectNameAutoComplete}
        <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    );

    const desktopFilters = (
      <div className={classes.desktopFilters}>
        {renderProjectNameAutoComplete}
        {renderDialogFilters}
      </div>
    );
    return isMobileWidth() ? mobileFilters : desktopFilters;
  };

  const renderDialogFilters = (
    <>
      <SelectComponent
        label={"Status"}
        value={statusFilter}
        setValue={(value) => {
          setStatusFilter(value);
          searchParams.set("status", value);
          updateUrl();
        }}
        options={Object.values(AssignationStatusFilters)}
        size="small"
      />
      <FormGroup>
        <SwitchCustom
          checked={assigned}
          onChange={handleChangeAssignedProjectsByPosittion}
          size="medium"
          color="primary"
          label="Assigned to me"
        />
      </FormGroup>
    </>
  );

  function getAdditionalActions(instance: Assignation): ActionsMenuEntryIcons[] {
    if (!canEdit) return (
      [{
        title: t("N/A"),
        action: () => {},
      }]
    )
    const options = [
      {
        title: t("Delete"),
        action: () => setSelectedAssignation(instance),
        icon:<DeleteOutlineIcon/>
      },
      {
        title: t("Transfer to another position"),
        action: () => setTransferAssignation(instance),
        icon:<MoveUpIcon/>
      },
      {
        title: t("Edit"),
        action: () => {
          setSelectedEditElement(instance);
          setIsEditing(true);
        },
        icon:<EditIcon/>
      },
    ];

    return options;
  }

  function handleOnCancel(): void {
    setIsEditing(false)
  }

  return (
    <>
      <AppContainerFullHeight
        bottomSectionRef={bottomSectionRef}
        topSection={
          <AppToolbar elevation={0} title={t("Assignation List")}>
            {renderFilters()}
          </AppToolbar>
        }
        bottomSection={
          <Box sx={{ boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)", borderRadius: "10px" }}>
            <SpinnerWrapper isLoading={isLoading}>
              <TableBasedCrud<Assignation>
                headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
                buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
                cancelButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
                dialogStyles={ customStyles.root }
                buttonContainer={ customButtonsContainer }
                gridButtonContainer={ gridButtonContainer }
                addButtonBuilder={ customButtonBuilder }
                maxHeight={calculateMaxHeight(canEdit)}
                stickyHeader
                stickyPagination
                elementName={t("Assignation")}
                setIsEditModeActive={setEditMode}
                rowsPerPage={ROWS_PER_PAGE}
                denseTable
                enablePagination
                stylesForHeader={{
                  fontSize: "1.1em",
                }}
                forceUpdateOn={update}
                onDataFetch={(page, count) => getAssignations(page, count)}
                onCreate={canEdit ? handleOnCreate : undefined}
                onUpdate={handleOnUpdate}
                validateBeforeClosing={true}
                disableSaveButton={disableSaveButton}
                onCancelDialog={() => {
                  setDisableSaveButton(false);
                }}
                renderDialogContent={(register, errors, control) => (
                  <>
                    {!editMode && (
                      <>
                        <AutoCompleteComponent
                          control={control}
                          register={register}
                          errors={errors}
                          propertyName={"userExternalId"}
                          label="User Name"
                          required={true}
                          options={userNames}
                        />
                        <AutoCompleteComponent
                          control={control}
                          register={register}
                          errors={errors}
                          error={positionNames.length === 0 || undefined}
                          readOnly={positionNames.length === 0}
                          propertyName="position.externalId"
                          label={positionNames.length === 0 ? "No positions available" : "Position Name"}
                          required={true}
                          options={positionNames}
                          onChange={(data) => setPositionExternalId(data ? data.externalId : "")}
                        />
                      </>
                    )}
                    <AssignationEditionFields
                      control={control}
                      register={register}
                      errors={errors}
                      onStartDateChange={(date) => {
                        setAssignationStartDate(date.toString());
                      }}
                      onEndDateChange={(date) => {
                        setAssignationEndDate(date.toString());
                      }}
                    />
                  </>
                )}
                resetValue={{ userExternalId: "", positionExternalId: "", endDate: null }}
                tableColumns={[
                  {
                    header: t("User Name"),
                    content: (instance) => (
                      <>
                        <Link to={`${Routes.PROFILE}/${instance.userExternalId}`}>{instance.userFullName}</Link>
                      </>
                    ),
                  },
                  {
                    header: t("Position"),
                    content: (instance) => (
                      <>
                        <Link to={`${Routes.POSITIONS}?project=${selectedProject?.externalId}`}>{instance.position.internalRoleName}</Link>
                      </>
                    ),
                  },
                  {
                    header: t("Internal hours"),
                    content: (instance) => <>{instance.position.internalHours}</>,
                  },
                  {
                    header: t("External hours"),
                    content: (instance) => <>{instance.position.externalHours}</>,
                  },
                  {
                    header: t("Start Date"),
                    content: (instance) => <>{OpsDateUtils.formatUTCAsDDMMMYYY(instance.shortStartDate)}</>,
                  },
                  {
                    header: t("End Date"),
                    content: (instance) => <>{instance.shortEndDate ? OpsDateUtils.formatUTCAsDDMMMYYY(instance.shortEndDate) : ""}</>,
                  },
                  {
                    header: t("Actions"),
                    content: (instance) => {
                      const actions = getAdditionalActions(instance)
                      return (<>
                        {actions.map((action) => {return (<ActionButton onClick={action.action} tooltip={action.title} icon={action.icon}/>)})}
                        </>
                      )
                    },
                  },
                ]}
                stylesForRowIfCondition={(row) => {
                  if (row.shortEndDate && isAfter(new Date(), new Date(row.shortEndDate))) {
                    return { backgroundColor: ColorUtils.LIGHT_GRAY };
                  }
                }}
                hideActions={true}  // Hide default Actions column
                buildAdditionalActions={(instance) => {
                  return [
                    {
                      title: t("Delete"),
                      action: () => setSelectedAssignation(instance),
                    },
                    {
                      title: t("Transfer to another position"),
                      action: () => setTransferAssignation(instance),
                    },
                  ];
                }}
              />
            </SpinnerWrapper>
          </Box>
        }
      />

      {/* Dialog to delete assignations */}
      <AppDialog
        open={!!selectedAssignation}
        onClose={() => setSelectedAssignation(undefined)}
        title={"Delete"}
        content={
          <Typography>
            {`The next assignation is going to be eliminated: ${selectedAssignation?.position.externalRoleName} - ${selectedAssignation?.userFullName}.
            Do you want to confirm this action?`}
          </Typography>
        }
        buttons={[
          {
            label: "Confirm delete",
            onClick: () => {
              handleOnDeleteAssignation();
              setUpdate(Date.now());
            },
          },
        ]}
      />

      {/* Dialog to manage filters  */}
      <FilterDialog title={"Assignations"} setIsOpen={setIsOpen} isOpen={isOpen}>
        {renderDialogFilters}
      </FilterDialog>

      {/* Dialog to manage assignation transfers */}
      <AssignationTransferDialog
        onClose={() => {
          setTransferAssignation(undefined);
        }}
        onSave={(formData) => {
          handleTransferAssignation(formData);
        }}
        isLoading={false}
        positionNames={transferPositionNames}
        projectsNames={RoleUtils.isAdmin() ? projectsNames : managerProjects}
        open={transferAssignation !== undefined}
        assignation={transferAssignation}
        currentProject={transferProject}
        handleChangeProject={(project: AutoCompleteUIDTO) => handleTransferProjectChange(project)}
      />

      {/* Copy of the Dialog to Edit -- To use with Edit Action Icon */}
      <EditDialog<Assignation>
        headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
        buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
        cancelButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
        dialogStyles={ customStyles.root }
        elementName={"Assignation"}
        dialogPosition={DialogPosition.Center}
        currentInstance={selectedEditElement}
        newItemDialogOpen={isEditing}
        onCreate={canEdit ? handleOnCreate : undefined}
        onUpdate={canEdit ? handleOnUpdate : undefined}
        onCancel={handleOnCancel}
        renderDialogContent={(register, errors, control) => (
          <>
          <AssignationEditionFields
            control={control}
            register={register}
            errors={errors}
            onStartDateChange={(date) => {
              date && setAssignationStartDate(date.toString());
            }}
            onEndDateChange={(date) => {
              date && setAssignationEndDate(date.toString());
            }}
          />
        </>
        )}
        setNewInstanceDialogOpen={() => {}}
        setLastUpdateTimestamp={() => {}}
      />
    </>
  );
}

const useStyles = makeStyles(() => ({
  desktopFilters: {
    borderRadius: "4px",
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1rem',
    border: `1px solid ${ColorUtils.LIGHTER_GRAY}`,
    boxSizing: 'inherit',
    boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)",
    "&>div": {
      margin: "0 !important",
    },
    display: "flex",
    flexDirection: "row",
    columnGap: ".8rem",
    "&> div:first-child": {
      minWidth: "150px",
      marginLeft: "0",
    },
    "&> div:nth-child(2)": {
      minWidth: "150px",
    },
  },
  mobileFilters: {
    "&> div:first-child": {
      minWidth: "150px",
      width: "100%",
      marginLeft: "0",
    },
    padding: "5px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  inputs: {
    "&.MuiInputBase-root": {
      backgroundColor: "none",
    },
  },
}),
{ index: 1 }
);
